import React from "react";
import { Check } from "lucide-react";

// A compact, self-styled selection toggle for the Actions bar — deliberately
// NOT a raw <input type="checkbox"> next to a label, since that reads as an
// afterthought bolted onto a row of pill-shaped action buttons. Instead this
// renders as one small pill matching the existing button family (rounded,
// bordered, hover-lift) that shows its own checked/indeterminate state via a
// tiny custom glyph, plus a "Clear" link that only appears once something is
// actually selected.
//
// `onToggle` selects every row in `totalFilteredCount` (the FULL filtered
// result set, not just the rows visible on the current page) when nothing/
// some are selected, and clears the selection when everything is selected —
// pagination-proof by construction, since the caller always computes
// allSelected/someSelected against the filtered array, never the paged slice.
const PayrollSelectionTool = ({
  t,
  theme,
  currentTheme,
  isRTL,
  selectedCount,
  totalFilteredCount,
  allSelected,
  someSelected,
  onToggle,
  onClear,
}) => {
  if (!totalFilteredCount) return null;

  const isActive = allSelected || someSelected;
  const glyphColor = isActive ? currentTheme.colors.primary : currentTheme.colors.textSecondary;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexDirection: isRTL ? "row-reverse" : "row",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        title={
          allSelected
            ? t("clearSelection")
            : `${t("selectAllFiltered")} (${totalFilteredCount})`
        }
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexDirection: isRTL ? "row-reverse" : "row",
          padding: "0.55rem 0.9rem",
          borderRadius: "0.85rem",
          fontSize: "0.85rem",
          fontWeight: 600,
          cursor: "pointer",
          border: `1px solid ${
            isActive
              ? `${currentTheme.colors.primary}55`
              : currentTheme.colors.border
          }`,
          background: isActive
            ? `${currentTheme.colors.primary}14`
            : theme === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.03)",
          color: isActive ? currentTheme.colors.primary : currentTheme.colors.textSecondary,
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = isActive
            ? `${currentTheme.colors.primary}22`
            : theme === "dark"
              ? "rgba(255,255,255,0.09)"
              : "rgba(0,0,0,0.06)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isActive
            ? `${currentTheme.colors.primary}14`
            : theme === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.03)";
        }}
      >
        <span
          style={{
            width: "15px",
            height: "15px",
            borderRadius: "4px",
            border: `1.5px solid ${glyphColor}`,
            background: allSelected ? glyphColor : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "all 0.2s ease",
          }}
        >
          {allSelected && (
            <Check
              size={10}
              strokeWidth={3.5}
              color={theme === "dark" ? "#0b1220" : "#ffffff"}
            />
          )}
          {someSelected && !allSelected && (
            <span
              style={{
                width: "7px",
                height: "2px",
                borderRadius: "1px",
                background: glyphColor,
              }}
            />
          )}
        </span>
        <span>
          {selectedCount > 0
            ? `${selectedCount} ${t("selected")}`
            : t("selectAllFiltered")}
        </span>
        {selectedCount > 0 && (
          <span style={{ opacity: 0.6 }}>/ {totalFilteredCount}</span>
        )}
      </button>

      {selectedCount > 0 && (
        <button
          type="button"
          onClick={onClear}
          style={{
            border: "none",
            background: "transparent",
            color: currentTheme.colors.textMuted,
            fontSize: "0.8rem",
            fontWeight: 600,
            cursor: "pointer",
            padding: "0.3rem 0.2rem",
            textDecoration: "underline",
            textUnderlineOffset: "2px",
          }}
        >
          {t("clearSelection")}
        </button>
      )}
    </div>
  );
};

export default PayrollSelectionTool;
